/**
 * Finance PDF extraction API client.
 *
 * Backend routes (relative to the axios base /api):
 *   POST /finance-pdf/india-statements          — India annual report
 *   POST /finance-pdf/us-statements             — US 10-K / annual report
 *   GET  /finance-pdf/statements-jobs/{job_id}  — poll extraction job
 *
 * Extraction is asynchronous (typically 1–4 minutes for a large annual
 * report): the POST returns 202 with a job_id as soon as the upload lands;
 * the UI polls the job endpoint every 3–5 s. On "completed" the job's
 * result.persistence.company_id is the companyId to navigate to
 * ("ind_pdf_*", "uk_pdf_*", or "comp_us_*" for US) — the normal
 * company-detail view resolves it directly, because PDF companies share
 * exactly the same graph shape, property names and calculation engines as
 * scraped ones. No PDF-specific data plumbing exists beyond this file.
 */
import apiClient from '../api';

export type PdfStatementsCountry = 'INDIA' | 'US';

/**
 * Which set of audited statements to extract. Consolidated includes
 * subsidiaries; Standalone is the parent company only. Backend defaults to
 * consolidated-preferred when the field is omitted; anything else is a 400.
 */
export type StatementBasis = 'consolidated' | 'standalone';

export type PdfStatementsJobStatus = 'pending' | 'processing' | 'failed' | 'completed';

/** 202 body returned by the two statement-extraction POST routes. */
export interface PdfStatementsJobStart {
  job_id: string;
  status: PdfStatementsJobStatus;
  message?: string;
}

/** The slice of the completed extraction response the UI reads. */
export interface PdfStatementsResult {
  /** Fiscal years written to the graph. */
  years_extracted?: number[];
  persistence?: {
    /** The companyId to navigate to: ind_pdf_*, uk_pdf_*, comp_us_*. */
    company_id?: string | null;
    /** Confirms storage actually happened. */
    wrote_to_neo4j?: boolean;
  };
  [key: string]: unknown;
}

/** GET /finance-pdf/statements-jobs/{job_id} */
export interface PdfStatementsJob {
  job_id?: string;
  status: PdfStatementsJobStatus;
  country?: PdfStatementsCountry;
  filename?: string;
  created_at?: string;
  completed_at?: string | null;
  /** Full extraction response when completed. */
  result?: PdfStatementsResult | null;
  /**
   * Present when failed. May embed structured reasons: "company_ambiguous"
   * (multiple graph matches — user must disambiguate) and
   * "company_not_found" (unreadable PDF header).
   */
  error?: string | null;
  /** Older backend builds put the id at the top level — kept as fallback. */
  company_id?: string | null;
}

const COUNTRY_ROUTE: Record<PdfStatementsCountry, string> = {
  INDIA: '/finance-pdf/india-statements',
  US: '/finance-pdf/us-statements',
};

/**
 * Upload an annual-report PDF and start the extraction job.
 *
 * `basis` is forwarded as-is when provided; `dry_run` is only sent when
 * explicitly true — the UI leaves it unset (backend defaults to false).
 */
export const startPdfStatementsExtraction = async (
  country: PdfStatementsCountry,
  file: File,
  options?: { basis?: StatementBasis; dryRun?: boolean }
): Promise<PdfStatementsJobStart> => {
  const formData = new FormData();
  formData.append('file', file);
  if (options?.basis) formData.append('basis', options.basis);
  if (options?.dryRun === true) formData.append('dry_run', 'true');

  const response = await apiClient.post<PdfStatementsJobStart>(
    COUNTRY_ROUTE[country],
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      // Only the upload happens inside this request (the 202 comes straight
      // back); the allowance is for large annual-report PDFs on slow links.
      timeout: 300000,
    }
  );
  return response.data;
};

/** Poll the extraction job. Call every 3–5 s until completed/failed. */
export const getPdfStatementsJob = async (jobId: string): Promise<PdfStatementsJob> => {
  const response = await apiClient.get<PdfStatementsJob>(
    `/finance-pdf/statements-jobs/${encodeURIComponent(jobId)}`
  );
  return response.data;
};

/** The companyId a completed job persisted, wherever the backend put it. */
export const completedCompanyId = (job: PdfStatementsJob): string | null =>
  job.result?.persistence?.company_id ?? job.company_id ?? null;
